import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  Box,
  FormControl,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CountryCode {
  code: string;
  label: string;
  dialCode: string;
  flag: string;
}

const COUNTRY_CODES: CountryCode[] = [
  { code: 'CL', label: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'AR', label: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'PE', label: 'Perú', dialCode: '+51', flag: '🇵🇪' },
  { code: 'BR', label: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'UY', label: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'PY', label: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
  { code: 'BO', label: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
  { code: 'EC', label: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
  { code: 'CO', label: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'VE', label: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'MX', label: 'México', dialCode: '+52', flag: '🇲🇽' },
  { code: 'US', label: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
  { code: 'ES', label: 'España', dialCode: '+34', flag: '🇪🇸' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  autoComplete?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  label,
  required = false,
  disabled = false,
  name = 'phone',
  autoComplete = 'tel',
}) => {
  const { t } = useTranslation();

  // Parse the current value to extract country code and phone number
  const parsePhoneNumber = (phoneValue: string): { countryCode: string; phoneNumber: string } => {
    if (!phoneValue) {
      return { countryCode: '+56', phoneNumber: '' };
    }

    // Find matching country code
    const matchedCountry = COUNTRY_CODES.find(country =>
      phoneValue.startsWith(country.dialCode)
    );

    if (matchedCountry) {
      return {
        countryCode: matchedCountry.dialCode,
        phoneNumber: phoneValue.slice(matchedCountry.dialCode.length).trim(),
      };
    }

    // Default to Chile if no match
    return { countryCode: '+56', phoneNumber: phoneValue };
  };

  const { countryCode, phoneNumber } = parsePhoneNumber(value);

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const newCountryCode = event.target.value;
    const newValue = phoneNumber ? `${newCountryCode} ${phoneNumber}` : newCountryCode;
    onChange(newValue);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = event.target.value;
    // Remove any non-digit characters except spaces and dashes for formatting
    const cleanNumber = newPhoneNumber.replace(/[^\d\s-]/g, '');
    const newValue = cleanNumber ? `${countryCode} ${cleanNumber}` : countryCode;
    onChange(newValue);
  };

  return (
    <FormControl fullWidth error={error}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          sx={{
            minWidth: 120,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
          renderValue={(selected) => {
            const country = COUNTRY_CODES.find(c => c.dialCode === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{country?.flag}</span>
                <span>{country?.dialCode}</span>
              </Box>
            );
          }}
        >
          {COUNTRY_CODES.map((country) => (
            <MenuItem key={country.code} value={country.dialCode}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{country.flag}</span>
                <span>{country.dialCode}</span>
                <span style={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {country.label}
                </span>
              </Box>
            </MenuItem>
          ))}
        </Select>

        <TextField
          fullWidth
          label={label || t('client.phone')}
          name={name}
          type="tel"
          autoComplete={autoComplete}
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          onBlur={onBlur}
          error={error}
          required={required}
          disabled={disabled}
          placeholder="9 1234 5678"
        />
      </Box>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default PhoneInput;
